import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import {FaEraser, FaInfoCircle, FaPlus, FaSearch} from "react-icons/fa";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import LibraryGrid from "./components/library-grid.jsx";
import LibraryList from "./components/library-list.jsx";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import LibraryService from "../../../services/web/library-service.js";
import {Modal} from "../../../components/modal/modal.jsx";
import {toast} from "react-toastify";
import LibraryBreadcrumb from "./components/library-breadcrumb.jsx";
import {
    buildLibraryButtonOptions,
    confirmDeleteItem,
    confirmDeleteItems, getLibraryErrorMessage, newItemsButtonOptions,
    orderOptions, libraryTypeLabels,
    typeOptions, viewModeOptions, getLibrarySuccessMessage, getLibraryNameInputLabel
} from "./utils/library-utils.js";
import LibrarySelectionToolbar from "./components/library-selection-toolbar.jsx";
import groupService from "../../../services/web/group-service.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import useHasPermission from "../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../utils/enums/permission-enum.js";
import ProjectUtils from "../../../utils/project-utils.js";
import LibraryPreview from "./components/library-preview/library-preview.jsx";
import {useLocation, useSearchParams} from "react-router";
import DropdownButton from "../../../components/button/dropdown-button.jsx";
import ToggleButton from "../../../components/button/toggle-button.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const initialFilter = {
    search: "",
    orderBy: null,
    type: null,
}

const MAX_LIBRARY_FILE_SIZE = 50 * 1024 * 1024;

export default function Library() {
    const navigate = navigateHook();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const folderIdFromUrl = searchParams.get("folderId");
    const [viewMode, setViewMode] = useState("grid");
    const [currentFolderId, setCurrentFolderId] = useState(folderIdFromUrl ? Number(folderIdFromUrl) : null);
    const [openModal, setOpenModal] = useState(false);
    const [folderName, setFolderName] = useState("");
    const [operationGroup, setOperationGroup] = useState([]);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [previewModeEnabled, setPreviewModeEnabled] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const [uploadFeedbackStatus, setUploadFeedbackStatus] = useState("idle");
    const [formFilter, setFormFilter] = useState(initialFilter);
    const [filter, setFilter] = useState(initialFilter);

    const isEditingFolder = !!selectedFolder;
    const folderInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const uploadFeedbackTimeoutRef = useRef(null);
    const queryClient = useQueryClient();
    const hasPermission = useHasPermission();
    const canManageLibrary = hasPermission(PermissionEnum.LIBRARY_MANAGE);

    const isList = viewMode === "list";
    const isGrid = viewMode === "grid";

    useEffect(() => {
        setCurrentFolderId(folderIdFromUrl ? Number(folderIdFromUrl) : null);
        setBreadcrumbs(location.state?.breadcrumbs ?? []);
        setSelectedItemIds([]);
        setPreviewItem(null);
    }, [folderIdFromUrl, location.state]);

    useEffect(() => {
        setSelectedItemIds((prev) => prev.length ? [] : prev);
        setPreviewItem(null);
    }, [viewMode, currentFolderId, filter]);

    const isSearching = filter.search.length >= 1;

    const { data: items = [], isLoading: isLoadingItems, isFetching: isFetchingItems } = useQuery({
        queryKey: ["library-items", currentFolderId, filter.orderBy, filter.type],
        queryFn: () => LibraryService.listItems({
            folderId: currentFolderId,
            orderBy: filter.orderBy,
            type: filter.type
        }),
        enabled: !isSearching,
        placeholderData: keepPreviousData,
    });

    const invalidateCurrentLibraryQueries = useCallback(async () => {
        await queryClient.invalidateQueries({
            queryKey: ["library-items", currentFolderId]
        });

        await queryClient.invalidateQueries({
            queryKey: ["library-search"]
        });
    }, [queryClient, currentFolderId]);

    const { data: searchResults = [], isLoading: isLoadingSearch, isFetching: isFetchingSearch } = useQuery({
        queryKey: ["library-search", filter.search, filter.orderBy, filter.type],
        queryFn: () => LibraryService.searchItems({
            search: filter.search,
            orderBy: filter.orderBy,
            type: filter.type
        }),
        enabled: isSearching,
        placeholderData: keepPreviousData,
    });

    const { data: groupOptions } = useQuery({
        queryKey: ["group-options"],
        queryFn: async () => groupService.getOptionsMyUser(),
        enabled: canManageLibrary,
    });

    const displayItems = isSearching ? searchResults : items;
    const isLoadingDisplay = isSearching ? isLoadingSearch : isLoadingItems;
    const isFetchingDisplay = isSearching ? isFetchingSearch : isFetchingItems;

    const createFolderMutation = useMutation({
        mutationFn: LibraryService.createFolder,
        onSuccess: async () => {
            toast.success(getLibrarySuccessMessage("FOLDER", "create"));
            await invalidateCurrentLibraryQueries();
        },
        onError: (error) => {
            console.error("Erro ao criar pasta:", error.response?.data || error);
            const message = getLibraryErrorMessage(error);
            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    });

    const updateItemMutation = useMutation({
        mutationFn: ({ id, payload }) => LibraryService.updateItem({ id, payload }),
        onSuccess: async (_, item) => {
            toast.success(getLibrarySuccessMessage(item.type, "update"));
            await invalidateCurrentLibraryQueries();
        },
        onError: (error) => {
            console.error("Erro ao editar pasta: ", error.response?.data || error);
            const message = getLibraryErrorMessage(error);
            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    });

    const deleteItemMutation = useMutation({
        mutationFn: ({ id, confirmationText }) => LibraryService.deleteItem({id, confirmationText}),
        onSuccess: async (_, item) => {
            ProjectUtils.success(false, getLibrarySuccessMessage(item.type, "delete"));
            setSelectedFolder(null);
            await invalidateCurrentLibraryQueries();
        },
        onError: (error) => {
            console.error("Erro ao excluir item: ", error.response?.data || error);
            const message = getLibraryErrorMessage(error);
            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    });

    const deleteItemsMutation = useMutation({
        mutationFn: LibraryService.deleteItems,
        onSuccess: async () => {
            ProjectUtils.success(false, "Itens excluídos com sucesso");
            setTimeout(handleClearSelection, 1500);
            await invalidateCurrentLibraryQueries();
        },
        onError: (error) => {
            console.log("Erro ao excluir itens: ", error?.response?.data || error);
            const message = getLibraryErrorMessage(error);
            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    });

    const uploadFilesMutation = useMutation({
        mutationFn: async ({ parentId, files }) => {
            return Promise.all(
                files.map((file) => LibraryService.uploadFile({parentId, file}))
            )
        },
        onMutate: () => {
            clearTimeout(uploadFeedbackTimeoutRef.current);
            setUploadFeedbackStatus("loading");
        },
        onSuccess: async () => {
            await invalidateCurrentLibraryQueries();
            setUploadFeedbackStatus("success");
            uploadFeedbackTimeoutRef.current = setTimeout(() => setUploadFeedbackStatus("idle"), 1500);
        },
        onError: (error) => {
            setUploadFeedbackStatus("idle");
            console.error("Erro ao realizar upload: ", error.response?.data || error);
            const message = getLibraryErrorMessage(error);
            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    });

    useEffect(() => () => clearTimeout(uploadFeedbackTimeoutRef.current), []);

    useEffect(() => {
        if (!openModal) return;

        setTimeout(() => {
            folderInputRef.current?.focus();
            folderInputRef.current?.select();
        }, 100);
    }, [openModal]);

    const handleSaveItem = useCallback(() => {
        const name = folderName.trim();

        const payload = {
            name,
            parentId: currentFolderId,
            operationGroupIds: operationGroup.map((group) => Number(group.value)),
        }

        if (isEditingFolder) {
            return updateItemMutation.mutateAsync({
                id: selectedFolder.id,
                type: selectedFolder.type,
                payload,
            });
        }

        return createFolderMutation.mutateAsync(payload);
    }, [folderName, currentFolderId, operationGroup, isEditingFolder, selectedFolder, updateItemMutation, createFolderMutation]);

    const handleFolderActionSuccess = useCallback(() => {
        setTimeout(() => {
            setOpenModal(false);
            setFolderName("");
            setOperationGroup([]);
            setSelectedFolder(null);
        }, 750);
    }, []);

    const handleDownloadItem = useCallback((item) => {
        if (!item?.id) {
            toast.error("Não foi possível identificar o arquivo.");
            return;
        }

        window.open(LibraryService.downloadItem(item.id));
    }, []);

    const folderHasChildren = useCallback(async (folderId) => {
        const folderItems = await LibraryService.listItems({ folderId });

        return folderItems.length > 0;
    }, []);

    const handleDeleteItem = useCallback(async (item) => {
        if (!item?.id) {
            ProjectUtils.swalErrorMessage("", "Não foi possível identificar a arquivo.", "Ok");
            return;
        }

        if (item.type === "FOLDER") {
            const hasChildren = await folderHasChildren(item.id);

            if (hasChildren) {
                ProjectUtils.swalErrorMessage("", "Esta pasta possui itens dentro dela. Exclua os itens para continuar.", "Ok");
                return;
            }
        }

        return confirmDeleteItem(item, () => deleteItemMutation.mutateAsync({
            id: item.id,
            type: item.type,
            confirmationText: "excluir",
        }));
    }, [deleteItemMutation, folderHasChildren]);

    const handleDeleteItems = useCallback(async () => {
        if (selectedItemIds.length === 0) {
            toast.error("Nenhum item selecionado.");
            return;
        }

        return confirmDeleteItems(selectedItemIds, () => deleteItemsMutation.mutateAsync({
            ids: selectedItemIds,
            confirmationText: "excluir",
        }));
    }, [selectedItemIds, deleteItemsMutation,]);

    const handleUploadFiles = useCallback((files) => {
        const fileList = Array.from(files ?? []);

        if (fileList.length === 0) return;

        const invalidFiles = fileList.filter((file) => file.size > MAX_LIBRARY_FILE_SIZE);

        if (invalidFiles.length > 0) {
            const message = invalidFiles.length === 1
                ? "Tamanho máximo de arquivo excedido. Limite: 50MB."
                : `Tamanho máximo de arquivos excedido. Limite: 50MB. ${invalidFiles.length} arquivos foram ignorados.`;

            ProjectUtils.swalErrorMessage("",  message, "Ok");

            return;
        }

        uploadFilesMutation.mutate({
            parentId: currentFolderId,
            files: fileList,
        });
    }, [currentFolderId, uploadFilesMutation]);

    const handleOpenFIleSelector = useCallback(() => {
        if (isSearching || !canManageLibrary) return;

        fileInputRef.current?.click();
    }, [isSearching, canManageLibrary]);

    const handleFileInputChange = useCallback((event) => {
        const files = event.target.files;

        if (!files || files.length === 0) return;

        handleUploadFiles(files);

        event.target.value = "";
    }, [handleUploadFiles]);

    const handleClearSelection = useCallback(() => {
        setSelectedItemIds([]);
        setPreviewItem(null);
    }, []);

    const handleOpenFolder = useCallback((folder) => {
        handleClearSelection();

        const folderBreadcrumb = {
            id: folder.id,
            name: folder.name,
            operationGroups: folder.operationGroups ?? [],
        };
        const newBreadcrumbs = isSearching
            ? [folderBreadcrumb]
            : [...breadcrumbs, folderBreadcrumb];

        setCurrentFolderId(folder.id);
        setBreadcrumbs(newBreadcrumbs);
        setFormFilter(initialFilter);
        setFilter(initialFilter);

        setSearchParams(
            { folderId: String(folder.id) },
            { state: { breadcrumbs: newBreadcrumbs } }
        );
    }, [breadcrumbs, handleClearSelection, isSearching, setSearchParams]);

    const handleEditFolder = useCallback((folder) => {
        setSelectedFolder(folder);
        setFolderName(folder.name);
        setOperationGroup(folder.operationGroups ?? []);
        setOpenModal(true);
    }, []);

    const handleBreadcrumbClick = useCallback((index) => {
        handleClearSelection();

        if (index === -1) {
            setCurrentFolderId(null);
            setBreadcrumbs([]);
            setSearchParams({}, { state: { breadcrumbs: [] } });
            return;
        }

        const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
        const selectedFolder = newBreadcrumbs[newBreadcrumbs.length - 1];

        setCurrentFolderId(selectedFolder.id);
        setBreadcrumbs(newBreadcrumbs);
        setSearchParams(
            { folderId: String(selectedFolder.id) },
            { state: { breadcrumbs: newBreadcrumbs } }
        );
    }, [handleClearSelection, breadcrumbs, setSearchParams]);

    const handleSelectItem = useCallback((item, event) => {
        event.stopPropagation();

        const isCtrlPressed = event.ctrlKey || event.metaKey;

        if (!isCtrlPressed) {
            setSelectedItemIds([item.id]);

            if (previewModeEnabled) {
                setPreviewItem(item);
            }

            return;
        }

        setSelectedItemIds((prev) => {
            const alreadySelected = prev.includes(item.id);

            if (alreadySelected) {
                if (previewItem?.id === item.id) {
                    setPreviewItem(null);
                }

                return prev.filter((id) => id !== item.id);
            }

            if (previewModeEnabled) {
                setPreviewItem(item);
            }

            return [...prev, item.id];
        });
    }, [previewModeEnabled, previewItem]);

    const selectedItems = useMemo(() => {
        const selectedIds = new Set(selectedItemIds);

        return displayItems.filter((item) => selectedIds.has(item.id));
    }, [displayItems, selectedItemIds]);

    const buttonOptions = buildLibraryButtonOptions({
        onRename: canManageLibrary ? handleEditFolder : undefined,
        onDownload: handleDownloadItem,
        onDelete: canManageLibrary ? handleDeleteItem : undefined,
    });

    const currentParentFolder = useMemo(() => {
        if (breadcrumbs.length === 0) return null;

        return breadcrumbs[breadcrumbs.length - 1];
    }, [breadcrumbs]);

    const newFolder = useCallback(() => {
        setSelectedFolder(null);
        setFolderName("");
        setOperationGroup(currentParentFolder?.operationGroups ?? []);
        setOpenModal(true);
    }, [currentParentFolder]);

    const handleTogglePreviewMode = useCallback(() => {
        setPreviewModeEnabled((prev => {
            const nextValue = !prev;

            if (!nextValue) {
                setPreviewItem(null);
                return nextValue;
            }

            const selectedItem = displayItems.find((item) => selectedItemIds.includes(item.id));

            setPreviewItem(selectedItem ?? null);

            return nextValue;
        }));
    }, [displayItems, selectedItemIds]);

    const handleSearch = () => {
        const search = formFilter.search.trim();

        setFilter({
            ...formFilter,
            search,
        });

        setSelectedItemIds([]);
        setPreviewItem(null);
    }

    const handleClearFilters = () => {
        setFormFilter(initialFilter);
        setFilter(initialFilter);
        setSelectedItemIds([]);
        setPreviewItem(null);
    }

    useGlobalKey("Enter", handleSearch);
    useGlobalKey("Escape", handleClearFilters);

    const newDoc = () => {
        navigate(`/library/document/form?parentId=${currentFolderId}`, true);
    }

    const newItemOptions = newItemsButtonOptions({
        onNewFolder: canManageLibrary ? newFolder : undefined,
        onNewDoc: canManageLibrary
            ? newDoc
            : undefined,
        onNewFile: canManageLibrary ? handleOpenFIleSelector : undefined,
    });

    const isFolderItem = selectedFolder?.type === "FOLDER";
    const selectedItemType = selectedFolder?.type ?? "FOLDER";

    const clearSelectionIgnoreSelectors = [
        "[data-library-item]",
        "[data-library-actions]",
        ".ag-row",
        "[data-aggrid-actions]",
    ];

    const shouldIgnoreClearSelection = (target) => {
        return clearSelectionIgnoreSelectors.some((selector) =>
            target.closest(selector)
        );
    };

    const handleCardBodyClick = useCallback((event) => {
        if (shouldIgnoreClearSelection(event.target)) return;
        handleClearSelection();
    }, [handleClearSelection]);

    const isUploadingFiles = uploadFilesMutation.isPending;

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={handleFileInputChange}
            />
            <Template isLoading={isLoadingDisplay || isUploadingFiles}>
                <Template.Header>
                    <Template.Title>Biblioteca</Template.Title>
                    <div className="flex gap-2 justify-end">
                        <div className="flex gap-2 items-center!">
                            <ToggleButton
                                viewMode={viewMode}
                                setViewMode={setViewMode}
                                options={viewModeOptions}
                            />
                            <button
                                type="button"
                                title="Mostrar detalhes"
                                onClick={handleTogglePreviewMode}
                                className={[
                                    "flex h-8 w-8 items-center justify-center rounded-md cursor-pointer transition-colors hover:bg-[#e5e7eb]",
                                    previewModeEnabled
                                        ? "bg-[#e5e7eb]"
                                        : "bg-none"
                                ].join(" ")}
                            >
                                <FaInfoCircle size={22} />
                            </button>
                        </div>
                        {canManageLibrary && (
                            currentFolderId ? (
                                <DropdownButton
                                    items={newItemOptions}
                                    menuWidth={180}
                                    buttonClassName={'select-none'}
                                    renderButton={({ ref, ...buttonProps }) => (
                                        <Button
                                            {...buttonProps}
                                            reference={ref}
                                            target="primary"
                                            status={uploadFeedbackStatus}
                                            loading={isUploadingFiles}
                                            loadingText="Enviando..."
                                            successText="Upload concluído com sucesso"
                                            successClassName="disabled:!bg-green-700 disabled:opacity-100!"
                                        >
                                            <FaPlus /> Adicionar
                                        </Button>
                                    )}
                                />
                            ) : (
                                <Button
                                    target="primary"
                                    onClick={newFolder}
                                    buttonClassName={'select-none'}
                                >
                                    <FaPlus /> Adicionar pasta
                                </Button>
                            )
                        )}
                    </div>
                </Template.Header>
                <Template.Body className="space-y-2">
                    <Card>
                        <Card.Body className="flex gap-4">
                            <InputForm
                                id="search"
                                type="search"
                                value={formFilter.search}
                                onChange={(event) => setFormFilter((prev) => ({ ...prev, search: event.target.value }))}
                                label={
                                    <span className="flex gap-2 items-center select-none">
                                        <FaSearch /> Buscar por título, pasta ou conteúdo...
                                    </span>
                                }
                                classNameWrapper="flex-2"
                            />
                            <SelectComponent
                                id="orderBy"
                                label="Ordenar"
                                classNameWrapper="flex-1 select-none"
                                value={formFilter.orderBy}
                                onChange={(option) => setFormFilter((prev) => ({...prev, orderBy: option?.value ?? null }))}
                                options={orderOptions}
                                formatOptionLabel={(option) => {
                                    const Icon = option.icon;
                                    return (
                                        <div className="flex gap-2 items-center">
                                            <Icon size={option.iconSize} />
                                            {option.label}
                                        </div>
                                    )
                                }}
                            />
                            <SelectComponent
                                id="typeFile"
                                label="Tipo de arquivo"
                                classNameWrapper="flex-1 select-none"
                                value={formFilter.type}
                                onChange={(option) => setFormFilter((prev) => ({ ...prev, type: option?.value ?? null }))}
                                options={typeOptions}
                                formatOptionLabel={(option) => {
                                    const Icon = option.icon;
                                    return (
                                        <div className="flex gap-2 items-center">
                                            <Icon size={option.iconSize} />
                                            {option.label}
                                        </div>
                                    )
                                }}
                            />
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    onClick={handleClearFilters}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                    <div className="flex gap-2">
                        <Card className="min-w-0 flex-1">
                            <Card.Body
                                className="h-[calc(100vh-133px)]"
                                onClick={handleCardBodyClick}
                            >
                                <nav className="mb-2 flex min-w-0 items-center justify-between gap-2 overflow-hidden">
                                    <div className="min-w-0 flex-1 overflow-hidden select-none">
                                        {isSearching ? (
                                            <div className="items-center text-[18px]! font-bold">
                                                <span>Resultados da pesquisa</span>
                                            </div>
                                        ) : (
                                            <LibraryBreadcrumb
                                                breadcrumbs={breadcrumbs}
                                                onRootClick={() => handleBreadcrumbClick(-1)}
                                                onFolderClick={handleBreadcrumbClick}
                                                maxVisibleItems={3}
                                            />
                                        )}
                                    </div>
                                    <div className="shrink-0 whitespace-nowrap">
                                        <LibrarySelectionToolbar
                                            selectedItems={selectedItems}
                                            onDeleteItems={handleDeleteItems}
                                            onClearSelection={handleClearSelection}
                                            hasPermission={canManageLibrary}
                                        />
                                    </div>
                                </nav>
                                    {isList && (
                                        <LibraryList
                                            items={displayItems}
                                            isSearching={isSearching}
                                            isLoading={isLoadingDisplay}
                                            isFetching={isFetchingDisplay}
                                            hasPermission={canManageLibrary}
                                            onOpenFolder={handleOpenFolder}
                                            onDropFile={isSearching || !canManageLibrary ? undefined : handleUploadFiles}
                                            buttonOptions={buttonOptions}
                                            selectedItemIds={selectedItemIds}
                                            onSelectItem={handleSelectItem}
                                        />
                                    )}

                                    {isGrid && (
                                        <LibraryGrid
                                            items={displayItems}
                                            isSearching={isSearching}
                                            isLoading={isLoadingDisplay}
                                            isFetching={isFetchingDisplay}
                                            hasPermission={canManageLibrary}
                                            onOpenFolder={handleOpenFolder}
                                            onDropFile={isSearching || !canManageLibrary ? undefined : handleUploadFiles}
                                            buttonOptions={buttonOptions}
                                            selectedItemIds={selectedItemIds}
                                            onSelectItem={handleSelectItem}
                                        />
                                    )}
                            </Card.Body>
                        </Card>
                        {previewModeEnabled && (
                            <LibraryPreview
                                item={previewItem}
                                onClose={() => setPreviewItem(null)}
                            />
                        )}
                    </div>
                </Template.Body>
            </Template>
            <Modal
                open={openModal}
                setOpen={setOpenModal}
                title={isEditingFolder ? "Renomear" : "Nova pasta"}
                className="w-4/12"
                isCloseClick={false}
            >
                <Modal.Body className="space-y-4">
                    <InputForm
                        label={getLibraryNameInputLabel(selectedItemType)}
                        id="folderName"
                        value={folderName}
                        onChange={(event) => setFolderName(event.target.value)}
                    />
                    {(!isEditingFolder || isFolderItem) && (
                        <SelectComponent
                            id="operationGroup"
                            value={operationGroup}
                            options={groupOptions}
                            label="Grupos de operações"
                            onChange={setOperationGroup}
                            isMulti
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={handleSaveItem}
                        actionFeedbackType="save"
                        loading={createFolderMutation.isPending || updateItemMutation.isPending}
                        loadingText={isEditingFolder ? "Salvando..." : "Criando..."}
                        successText={isEditingFolder ? "Salvo com sucesso" : "Pasta criada com sucesso"}
                        onActionFeedbackSuccess={handleFolderActionSuccess}
                    >
                        {isEditingFolder ? "Salvar" : "Criar"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}